import { AdminUser } from 'tools/bff/generated/bff/admin/admin_user_pb';
import { Checkbox, Grid, Modal, TagPicker, Text } from '@tocktix/pantry';
import { Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { FunctionComponent, PropsWithChildren, useState } from 'react';
import { StoreType, useActions } from 'utils/connect';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import { userClient } from 'tools/bff/client';
import _ from 'lodash';

export interface EditUserDetailsFormValues {
  isConcierge: boolean;
  conciergeGroups: string[];
}

interface EditUserDetailsModalProps {
  user: AdminUser;
  isOpen: boolean;
  onClose(): void;
  onSubmit(): Promise<void>;
}

const validate = (values: EditUserDetailsFormValues) => {
  const errors: FormikErrors<EditUserDetailsFormValues> = {};
  console.log('values', values);
  if (values.isConcierge && values.conciergeGroups.length === 0) {
    errors.conciergeGroups = 'Concierge group is required';
  }
  return errors;
};

const EditUserDetailsModal: FunctionComponent<PropsWithChildren<EditUserDetailsModalProps>> = ({
  user,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);
  const partners = useSelector((state: StoreType) => state.partners.partners);

  const { actions } = useActions();

  useMountEffect(() => {
    actions.partners.fetchAll();
  });

  const sortedPartners = _.sortBy(partners, 'name');

  const handleClose = (resetForm: () => void) => {
    setError(null);
    resetForm();
    onClose();
  };

  const handleFormSubmit = (
    values: EditUserDetailsFormValues,
    formikHelpers: FormikHelpers<EditUserDetailsFormValues>
  ) => {
    if (!user.gdusUser?.gdusUuid) {
      setError('Error updating user: missing GDUS UUID');
      return Promise.reject();
    }

    return userClient
      .updateUser({
        uuid: user.gdusUser.gdusUuid,
        conciergeGroupNames: values.isConcierge ? values.conciergeGroups : [],
      })
      .then((response) => {
        if (response.response.case === 'successResponse') {
          return onSubmit();
        } else {
          setError('Error updating user: ' + response.response.value?.error);
          return Promise.reject();
        }
      })
      .then(() => handleClose(formikHelpers.resetForm))
      .catch((reason) => setError('Error updating user: ' + reason));
  };

  const initialConciergeGroups =
    user.tockPatron?.partners?.map((partner) => partner.name ?? '') ?? [];

  return (
    <Formik<EditUserDetailsFormValues>
      initialValues={{
        isConcierge: user.resyUser?.isConcierge ?? user.tockPatron?.canBookForGuest ?? false,
        conciergeGroups: initialConciergeGroups,
      }}
      validate={validate}
      onSubmit={handleFormSubmit}
      enableReinitialize
    >
      {({
        errors,
        handleSubmit,
        dirty,
        isValid,
        isSubmitting,
        setFieldValue,
        setValues,
        touched,
        values,
        resetForm,
      }) => (
        <Form>
          <Modal
            id="edit-user-details-modal"
            open={isOpen}
            onClose={() => handleClose(resetForm)}
            size="md"
          >
            <Modal.Header title="Edit account" />
            <Modal.Body>
              <Grid gap={100} alignItems="center">
                <Grid item span={3}>
                  <Checkbox
                    id="is-concierge-checkbox"
                    name="isConcierge"
                    checked={values.isConcierge}
                    value="isConcierge"
                    label="Is concierge"
                    onChange={(event) => {
                      const isConcierge = event.target.checked;
                      const conciergeGroups = isConcierge ? initialConciergeGroups : [];
                      setValues({ isConcierge, conciergeGroups });
                    }}
                  />
                </Grid>
                {values.isConcierge && (
                  <Grid item span={9} gap={50}>
                    <TagPicker
                      id="conciergeGroups"
                      label="Concierge groups"
                      currentValue={values.conciergeGroups.toString()}
                      onChange={(event: { currentTarget: { value: string; checked: boolean } }) => {
                        const selectedGroups = event.currentTarget.checked
                          ? [...values.conciergeGroups, event.currentTarget.value]
                          : values.conciergeGroups.filter(
                              (group) => group !== event.currentTarget.value
                            );
                        setFieldValue('conciergeGroups', selectedGroups);
                      }}
                    >
                      {sortedPartners.map((partner) => (
                        <TagPicker.Tag
                          key={partner.id}
                          value={partner.name}
                          label={partner.name}
                          checked={values.conciergeGroups.includes(partner.name)}
                        />
                      ))}
                    </TagPicker>
                    {touched.conciergeGroups && errors.conciergeGroups && (
                      <Text variant="body" color="fgDangerBase">
                        {errors.conciergeGroups}
                      </Text>
                    )}
                  </Grid>
                )}
                {error && (
                  <Grid item span={12}>
                    <Text variant="body" color="fgDangerBase">
                      {error}
                    </Text>
                  </Grid>
                )}
              </Grid>
            </Modal.Body>
            <Modal.Footer
              actions={{
                primary: {
                  label: isSubmitting ? 'Saving...' : 'Save',
                  type: 'submit',
                  disabled: !dirty || !isValid || isSubmitting,
                  onClick: () => handleSubmit(),
                },
                cancel: {
                  label: 'Cancel',
                  onClick: () => handleClose(resetForm),
                },
              }}
            />
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default EditUserDetailsModal;
